// GoogleImportButton.jsx
// Imports a business's recent Google reviews — but only after the business
// proves it owns the Google listing, by receiving a 6-digit code at the phone
// GOOGLE publishes for that listing (Phase 5.5). GBP signups already have their
// listing on file and skip straight to the code; everyone else pastes their
// Google URL once so we can find the listing. The profile phone is never used.
import { useState } from 'react';
import axios from 'axios';

const BRAND = '#00aef7';

function GoogleImportButton({ onReviewsImported, businessId }) {
  const [isOpen, setIsOpen] = useState(false);
  // phase: loading | url | send | code | importing
  const [phase, setPhase] = useState('loading');
  const [placeId, setPlaceId] = useState(null);
  const [maskedPhone, setMaskedPhone] = useState('');
  const [gbpUrl, setGbpUrl] = useState('');
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');

  const reset = () => {
    setPhase('loading'); setPlaceId(null); setMaskedPhone('');
    setGbpUrl(''); setCode(''); setError(''); setInfo('');
  };

  const open = async () => {
    setIsOpen(true);
    reset();
    try {
      const { data } = await axios.get('/api/import-google-reviews/status', { withCredentials: true });
      if (data.verified && data.has_listing) {
        await refresh();   // ownership already proven — just pull reviews, no code
      } else if (data.has_listing) {
        await resolve();   // listing known, ownership not yet proven -> verify
      } else {
        setPhase('url');
      }
    } catch {
      setPhase('url');
    }
  };

  const close = () => { setIsOpen(false); reset(); };

  // Resolve the listing (from a pasted URL, or the stored one) -> masked phone.
  const resolve = async (url) => {
    setIsLoading(true); setError(''); setInfo('');
    try {
      const { data } = await axios.post(
        '/api/gbp/verify/resolve',
        url ? { gbpUrl: url } : {},
        { withCredentials: true }
      );
      setPlaceId(data.place_id);
      setMaskedPhone(data.masked_phone);
      setPhase('send');
    } catch (err) {
      const d = err.response?.data;
      if (err.response?.status === 409 && d?.needs_url) { setPhase('url'); return; }
      setError(d?.message || d?.error || 'We couldn’t find your Google listing.');
      setPhase('url');
    } finally {
      setIsLoading(false);
    }
  };

  // Already-verified business: skip the code and just re-pull reviews (subject
  // to Google's 30-day cooldown).
  const refresh = async () => {
    setPhase('importing'); setIsLoading(true); setError(''); setInfo('');
    try {
      const { data } = await axios.post('/api/import-google-reviews/refresh', {}, { withCredentials: true });
      if (data.success) {
        setTimeout(() => {
          if (typeof onReviewsImported === 'function') onReviewsImported();
          else window.location.reload();
        }, 1500);
      } else {
        setPhase('cooldown');
        setInfo(data.next_import ? `You can import again on ${new Date(data.next_import).toLocaleDateString()}.` : 'Import is on cooldown.');
      }
    } catch (err) {
      const d = err.response?.data;
      if (err.response?.status === 429 && d?.next_import) {
        setPhase('cooldown');
        setInfo(`You can import again on ${new Date(d.next_import).toLocaleDateString()}.`);
      } else if (err.response?.status === 403 && d?.needs_verification) {
        await resolve(); // not actually verified — fall into the verify flow
      } else {
        setPhase('cooldown');
        setError(d?.error || 'Could not import reviews right now.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const findListing = () => {
    if (!gbpUrl.trim()) return;
    if (!gbpUrl.includes('google.com/maps') && !gbpUrl.includes('maps.google.com') && !gbpUrl.includes('maps.app.goo.gl')) {
      setError('Paste a URL from Google Maps (maps.google.com).');
      return;
    }
    resolve(gbpUrl.trim());
  };

  const sendCode = async (channel) => {
    setIsLoading(true); setError(''); setInfo('');
    try {
      const { data } = await axios.post(
        '/api/gbp/verify/start',
        { place_id: placeId, channel },
        { withCredentials: true }
      );
      setMaskedPhone(data.masked_phone || maskedPhone);
      setPhase('code');
      setInfo(channel === 'call'
        ? 'Calling your listing’s number with the code now…'
        : 'We texted a 6-digit code to your listing’s number.');
    } catch (err) {
      const d = err.response?.data;
      setError(d?.message || d?.error || 'Could not send the code. Try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const verify = async () => {
    if (!/^\d{4,8}$/.test(code.trim())) { setError('Enter the 6-digit code.'); return; }
    setIsLoading(true); setError(''); setInfo('');
    try {
      const { data } = await axios.post(
        '/api/gbp/verify/check',
        { place_id: placeId, code: code.trim() },
        { withCredentials: true }
      );
      if (!data.approved) { setError(data.message || 'That code is incorrect or expired.'); return; }
      setPhase('importing');
      setTimeout(() => {
        if (typeof onReviewsImported === 'function') onReviewsImported();
        else window.location.reload();
      }, 4000);
    } catch (err) {
      const d = err.response?.data;
      setError(d?.message || d?.error || 'Verification failed. Request a new code.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <div
        className="flex flex-col items-center justify-center p-8 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 transition-colors"
        onClick={open}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 48 48" className="mb-4">
          <path fill="#4285F4" d="M24 9.5c3.34 0 5.63 1.44 6.93 2.65l5.08-4.91C32.67 4.02 28.74 2 24 2 14.91 2 7.54 7.88 4.64 15.35l5.89 4.57C12.12 13.12 17.56 9.5 24 9.5z" />
          <path fill="#34A853" d="M46.5 24c0-1.53-.14-2.99-.4-4.4H24v8.33h12.7c-.55 3.02-2.19 5.58-4.7 7.31l7.38 5.71C43.98 36.9 46.5 30.97 46.5 24z" />
          <path fill="#FBBC05" d="M10.53 28.74a14.4 14.4 0 0 1 0-9.47l-5.89-4.57a23.953 23.953 0 0 0 0 18.61l5.89-4.57z" />
          <path fill="#EA4335" d="M24 46c6.48 0 11.91-2.15 15.88-5.86l-7.38-5.71c-2.04 1.37-4.65 2.19-8.5 2.19-6.44 0-11.88-3.62-14.46-8.77l-5.89 4.57C7.54 40.12 14.91 46 24 46z" />
        </svg>
        <p className="text-sm text-gray-500 text-center mt-2">
          Click here to import your 5 most recent Google Business reviews
        </p>
      </div>

      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96">
            {phase === 'loading' && (
              <p className="text-sm text-gray-500 text-center py-6">Checking your business…</p>
            )}

            {phase === 'url' && (
              <>
                <h3 className="text-lg font-bold mb-3" style={{ color: BRAND }}>Find your Google listing</h3>
                <label className="block text-sm font-medium text-gray-700 mb-2">Google Maps Business URL</label>
                <input
                  type="text"
                  value={gbpUrl}
                  onChange={(e) => setGbpUrl(e.target.value)}
                  placeholder="Paste your Google Business Profile URL"
                  className="w-full p-2 border border-gray-300 rounded focus:ring-blue-500 focus:border-blue-500"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Open your business on maps.google.com and copy the whole address bar. Next we’ll text a code to the phone on that listing to confirm it’s yours.
                </p>
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                <div className="flex justify-end space-x-2 mt-4">
                  <button onClick={close} className="px-4 py-2 text-gray-600 hover:text-gray-800" disabled={isLoading}>Cancel</button>
                  <button
                    onClick={findListing}
                    disabled={isLoading || !gbpUrl.trim()}
                    className="px-4 py-2 text-white rounded disabled:opacity-50"
                    style={{ background: BRAND }}
                  >
                    {isLoading ? 'Finding…' : 'Continue'}
                  </button>
                </div>
              </>
            )}

            {phase === 'send' && (
              <>
                <h3 className="text-lg font-bold mb-2" style={{ color: BRAND }}>Verify your business</h3>
                <p className="text-sm text-gray-600 mb-4">
                  We’ll send a 6-digit code to the phone number on your Google listing
                  {maskedPhone ? <> — <span className="font-semibold">{maskedPhone}</span></> : ''}. Only the owner can receive it.
                </p>
                {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
                <div className="flex flex-col gap-2">
                  <button onClick={() => sendCode('sms')} disabled={isLoading} className="w-full px-4 py-2 text-white rounded disabled:opacity-50" style={{ background: BRAND }}>
                    {isLoading ? 'Sending…' : 'Text me a code'}
                  </button>
                  <button onClick={() => sendCode('call')} disabled={isLoading} className="w-full px-4 py-2 text-gray-700 border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50">
                    Call me instead
                  </button>
                  <button onClick={close} disabled={isLoading} className="w-full px-4 py-2 text-gray-500 hover:text-gray-700">Cancel</button>
                </div>
              </>
            )}

            {phase === 'code' && (
              <>
                <h3 className="text-lg font-bold mb-2" style={{ color: BRAND }}>Enter your code</h3>
                {maskedPhone && <p className="text-sm text-gray-600 mb-3">Sent to {maskedPhone}.</p>}
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  placeholder="123456"
                  className="w-full p-2 border border-gray-300 rounded tracking-[0.5em] text-center text-lg focus:border-blue-500"
                />
                {info && <p className="text-xs text-gray-500 mt-2">{info}</p>}
                {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
                <div className="flex justify-between items-center mt-4">
                  <button onClick={() => sendCode('call')} disabled={isLoading} className="text-sm text-gray-500 hover:text-gray-700">Call me instead</button>
                  <div className="flex gap-2">
                    <button onClick={close} disabled={isLoading} className="px-4 py-2 text-gray-600 hover:text-gray-800">Cancel</button>
                    <button onClick={verify} disabled={isLoading || code.trim().length < 4} className="px-4 py-2 text-white rounded disabled:opacity-50" style={{ background: BRAND }}>
                      {isLoading ? 'Verifying…' : 'Verify'}
                    </button>
                  </div>
                </div>
              </>
            )}

            {phase === 'importing' && (
              <div className="text-center py-6">
                <p className="text-sm text-gray-600">Importing your Google reviews now…</p>
              </div>
            )}

            {phase === 'cooldown' && (
              <div className="text-center py-6">
                <p className="text-sm text-gray-700 mb-4">{error || info || 'Import is on cooldown.'}</p>
                <button onClick={close} className="px-4 py-2 text-white rounded" style={{ background: BRAND }}>OK</button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default GoogleImportButton;
